import { z } from "zod";

import { useRecordTenantStore } from "@/store/recordTenantStore";

/**
 * SERV-3232: a detail route opened from a merged-view row carries the row's entity as `?tenant=`,
 * so a reload or a pasted link reopens the record in its entity. Unknown keys pass through
 * because these routes also keep their own params (the tab, for one) in the search.
 */
export const RECORD_TENANT_SEARCH_FIELDS = {
  tenant: z.string().min(1).optional().catch(undefined),
};

export const recordTenantSearchSchema = z.object(RECORD_TENANT_SEARCH_FIELDS).passthrough();

export type RecordTenantSearch = { tenant?: string };

/**
 * The search a list link passes to a detail route. Only the merged view names the entity; anywhere
 * else the request scope already is the record's entity. The key is always present so a link
 * from inside another record never inherits that record's entity.
 */
export const recordTenantSearch = (
  mergedView: boolean,
  tenantId: string | null | undefined,
): RecordTenantSearch => ({ tenant: mergedView && tenantId ? tenantId : undefined });

/**
 * The entity a list row's action sends per call. Same rule as `recordTenantSearch`: only the merged
 * view names the row's entity, so inside an entity the request keeps today's scope.
 */
export const recordActingEntity = (
  mergedView: boolean,
  tenantId: string | null | undefined,
): string | undefined => (mergedView && tenantId ? tenantId : undefined);

/** Route `onEnter`/`onStay`: the open record's entity, or none. */
export const syncRecordTenant = ({ search }: { search: RecordTenantSearch }): void => {
  const { setRecordTenant, clearRecordTenant } = useRecordTenantStore.getState();
  if (search.tenant) setRecordTenant(search.tenant);
  else clearRecordTenant();
};

export const leaveRecordTenant = (): void => {
  useRecordTenantStore.getState().clearRecordTenant();
};
